import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule }                  from '@angular/common';
import { RouterOutlet, RouterLink, Router, NavigationEnd } from '@angular/router';
import { filter, Subject, takeUntil }   from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, AuthenticationResult } from '@azure/msal-browser';

import { HeaderComponent }     from './components/header/header.component';
import { StepperNavComponent } from './components/stepper-nav/stepper-nav.component';
import { SessionService }      from './core/services/session.service';

/** Routes on which the stepper nav should NOT be shown */
const NO_STEPPER_ROUTES = ['/', '/login-failed', '', '/chat'];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    HeaderComponent,
    StepperNavComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  /** Controls whether the stepper nav bar is rendered */
  showStepper = false;

  /** Hide the FAB button when we're already on the chat page */
  isChatPage = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly router:           Router,
    private readonly authService:      MsalService,
    private readonly broadcastService: MsalBroadcastService,
    readonly sessionService:           SessionService,
  ) {}

  extendSession(): void {
    this.sessionService.extendSession();
  }

  endSessionNow(): void {
    this.sessionService.showTimeoutWarning.set(false);
    // Delegate to the normal timeout flow
    (this.sessionService as any)._onInactivityTimeout?.();
  }

  /** Called from the session-expired screen — clears state and goes to landing. */
  startNewSession(): void {
    this.sessionService.showSessionExpired.set(false);
    this.sessionService.reset();
    window.location.href = '/';
  }

  ngOnInit(): void {
    // ── Handle Entra ID redirect response ──────────────────────────────
    // Must call handleRedirectObservable() in the root component so MSAL
    // can consume the redirect hash/code before any guard runs.
    this.authService.handleRedirectObservable().subscribe();

    // ── Log successful login events for debugging ───────────────────────
    this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(payload.account);
      });

    // ── Show/hide stepper based on current route ────────────────────────
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this._destroying$),
      )
      .subscribe((e) => {
        const nav  = e as NavigationEnd;
        const path = nav.urlAfterRedirects.split('?')[0];
        this.showStepper = !NO_STEPPER_ROUTES.includes(path);
        this.isChatPage  = path === '/chat';
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
